import { type ModuleCode, type Permission } from '@ibe/constants';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import { ApiError, type ApiClient } from '../lib/api-client.js';
import {
  can,
  canAll,
  canAny,
  hasModule,
  isMfaChallenge,
  type AuthenticatedUser,
  type LoginRequest,
  type LoginResponse,
  type MfaSetupResponse,
} from './session.js';

/**
 * Who is signed in, and how they sign in and out.
 *
 * The access token lives in the `ApiClient`, not in this state. Keeping it out
 * of React state means it is never serialised into a devtools snapshot, never
 * captured in an error report, and cannot be read off a rendered component
 * tree.
 */

export const AUTH_STATUS = {
  /** Trying the refresh cookie. Nothing is known yet. */
  RESTORING: 'RESTORING',
  AUTHENTICATED: 'AUTHENTICATED',
  ANONYMOUS: 'ANONYMOUS',
  /** Password cleared; user must enroll MFA (first login). */
  MFA_ENROLL: 'MFA_ENROLL',
  /** Password cleared; user must verify MFA (subsequent logins). */
  MFA_VERIFY: 'MFA_VERIFY',
} as const;

export type AuthStatus = (typeof AUTH_STATUS)[keyof typeof AUTH_STATUS];

export interface AuthContextValue {
  readonly status: AuthStatus;
  readonly user: AuthenticatedUser | null;
  /** Set when a session ended by itself, so the login page can explain why. */
  readonly endedReason: string | null;
  readonly login: (request: LoginRequest) => Promise<void>;
  readonly logout: () => Promise<void>;
  readonly can: (permission: Permission, propertyId?: string) => boolean;
  readonly canAll: (permissions: readonly Permission[]) => boolean;
  readonly canAny: (permissions: readonly Permission[]) => boolean;
  /** Whether this user's tenant bought a module. See `hasModule`. */
  readonly hasModule: (module: ModuleCode) => boolean;
  /** The MFA challenge token from the login step, set during MFA_ENROLL/MFA_VERIFY. */
  readonly mfaChallengeToken: string | null;
  /** Sets up MFA enrollment: returns QR code, secret and recovery codes. */
  readonly setupMfa: () => Promise<MfaSetupResponse>;
  /** Completes MFA enrollment with a TOTP code, finishing the session. */
  readonly enrollMfa: (code: string) => Promise<void>;
  /** Verifies a TOTP code to complete login for an already-enrolled user. */
  readonly verifyMfa: (code: string) => Promise<void>;
  /** Uses a recovery code to complete login without a TOTP device. */
  readonly recoverMfa: (recoveryCode: string) => Promise<void>;
  /** Cancels the MFA flow and returns to the password step. */
  readonly cancelMfa: () => void;
  readonly forgotPassword: (email: string) => Promise<{ expiresAt: string }>;
  readonly verifyOtp: (code: string) => Promise<void>;
  readonly resetPassword: (password: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const SESSION_EXPIRED = 'Your session ended. Sign in to continue.';

export interface AuthProviderProps {
  readonly client: ApiClient;
  readonly children: ReactNode;
}

export function AuthProvider({ client, children }: AuthProviderProps): React.JSX.Element {
  const [status, setStatus] = useState<AuthStatus>(AUTH_STATUS.RESTORING);
  const [user, setUser] = useState<AuthenticatedUser | null>(null);
  const [endedReason, setEndedReason] = useState<string | null>(null);
  const [mfaChallengeToken, setMfaChallengeToken] = useState<string | null>(null);

  /**
   * Restores the session from the refresh cookie.
   *
   * This runs before anything else renders, and until it settles the status is
   * `RESTORING` rather than `ANONYMOUS`. The difference matters: treating "not
   * yet known" as "signed out" makes every page reload flash the login screen,
   * and a protected route that redirects during that flash throws away the URL
   * the user actually asked for.
   */
  useEffect(() => {
    let cancelled = false;

    const restore = async (): Promise<void> => {
      // If we have a token from sessionStorage (e.g. platform operator without
      // a refresh cookie), restore directly without a server round-trip.
      if (client.accessToken !== null) {
        const storedUser = sessionStorage.getItem('ibe_user');
        if (storedUser !== null) {
          if (cancelled) return;
          try {
            setUser(JSON.parse(storedUser) as AuthenticatedUser);
            setStatus(AUTH_STATUS.AUTHENTICATED);
            return;
          } catch {
            // Corrupted user JSON — fall through to refresh cookie.
          }
        }
      }

      try {
        const restored = await client.post<LoginResponse>('/v1/auth/session');
        if (cancelled) return;

        client.setAccessToken(restored.accessToken);
        setUser(restored.user);
        setStatus(AUTH_STATUS.AUTHENTICATED);
      } catch {
        if (cancelled) return;
        // No cookie, an expired one, or the service is down. All three mean
        // the portal cannot show anything yet; the login page will say so.
        setStatus(AUTH_STATUS.ANONYMOUS);
      }
    };

    void restore();

    return () => {
      cancelled = true;
    };
    // The client is constructed once, at the application root, so this runs
    // once. It is listed rather than omitted because a lie in a dependency
    // array is a bug waiting for the day the client is recreated.
  }, [client]);

  const login = useCallback(
    async (request: LoginRequest): Promise<void> => {
      const result = await client.post<LoginResponse>('/v1/auth/login', request);

      if (isMfaChallenge(result)) {
        setMfaChallengeToken(result.mfaChallengeToken);
        setEndedReason(null);
        setStatus(
          result.mfaRequired === 'enroll' ? AUTH_STATUS.MFA_ENROLL : AUTH_STATUS.MFA_VERIFY,
        );
        return;
      }

      client.setAccessToken(result.accessToken);
      sessionStorage.setItem('ibe_user', JSON.stringify(result.user));
      setUser(result.user);
      setEndedReason(null);
      setMfaChallengeToken(null);
      setStatus(AUTH_STATUS.AUTHENTICATED);
    },
    [client],
  );

  const logout = useCallback(async (): Promise<void> => {
    try {
      // Best effort. The server revokes the refresh family and clears the
      // cookie; if that call fails the local session must still end, because
      // a user who pressed "sign out" on a shared machine and walked away has
      // to be signed out whatever the network did.
      await client.post('/v1/auth/logout');
    } catch (error) {
      if (!(error instanceof ApiError) && !(error instanceof Error)) throw error;
    } finally {
      client.setAccessToken(null);
      sessionStorage.removeItem('ibe_user');
      setUser(null);
      setEndedReason(null);
      setStatus(AUTH_STATUS.ANONYMOUS);
    }
  }, [client]);

  /** Applies a completed MFA session: stores the token and user, clears MFA state. */
  const completeMfaSession = useCallback(
    (session: LoginResponse): void => {
      client.setAccessToken(session.accessToken);
      sessionStorage.setItem('ibe_user', JSON.stringify(session.user));
      setUser(session.user);
      setEndedReason(null);
      setMfaChallengeToken(null);
      setStatus(AUTH_STATUS.AUTHENTICATED);
    },
    [client],
  );

  const setupMfa = useCallback(async (): Promise<MfaSetupResponse> => {
    if (mfaChallengeToken === null) {
      throw new Error('MFA setup requested without a challenge token.');
    }
    const result = await client.post<MfaSetupResponse>('/v1/auth/mfa/setup', {
      mfaChallengeToken,
    });
    return result;
  }, [client, mfaChallengeToken]);

  const enrollMfa = useCallback(
    async (code: string): Promise<void> => {
      if (mfaChallengeToken === null) {
        throw new Error('MFA enrollment requested without a challenge token.');
      }
      const session = await client.post<LoginResponse>('/v1/auth/mfa/enroll', {
        mfaChallengeToken,
        code,
      });
      completeMfaSession(session);
    },
    [client, mfaChallengeToken, completeMfaSession],
  );

  const verifyMfa = useCallback(
    async (code: string): Promise<void> => {
      if (mfaChallengeToken === null) {
        throw new Error('MFA verification requested without a challenge token.');
      }
      const session = await client.post<LoginResponse>('/v1/auth/mfa/verify', {
        mfaChallengeToken,
        code,
      });
      completeMfaSession(session);
    },
    [client, mfaChallengeToken, completeMfaSession],
  );

  const recoverMfa = useCallback(
    async (recoveryCode: string): Promise<void> => {
      if (mfaChallengeToken === null) {
        throw new Error('MFA recovery requested without a challenge token.');
      }
      const session = await client.post<LoginResponse>('/v1/auth/mfa/recover', {
        mfaChallengeToken,
        recoveryCode,
      });
      completeMfaSession(session);
    },
    [client, mfaChallengeToken, completeMfaSession],
  );

  const cancelMfa = useCallback((): void => {
    setMfaChallengeToken(null);
    setEndedReason(null);
    setStatus(AUTH_STATUS.ANONYMOUS);
  }, []);

  const forgotPassword = useCallback(
    async (email: string): Promise<{ expiresAt: string }> => {
      return await client.post<{ expiresAt: string }>('/v1/auth/forgot-password', { email });
    },
    [client],
  );

  const verifyOtp = useCallback(
    async (code: string): Promise<void> => {
      await client.post('/v1/auth/verify-otp', { code });
    },
    [client],
  );

  const resetPassword = useCallback(
    async (password: string): Promise<void> => {
      await client.post('/v1/auth/reset-password', { password });
    },
    [client],
  );

  /**
   * Ends the session when the client gives up on refreshing.
   *
   * Registered here rather than passed to the constructor so the callback can
   * reach React state. The client calls it at most once per expiry.
   */
  useEffect(() => {
    const onExpired = (): void => {
      sessionStorage.removeItem('ibe_user');
      setUser(null);
      setEndedReason(SESSION_EXPIRED);
      setStatus(AUTH_STATUS.ANONYMOUS);
    };

    return client.onSessionExpired(onExpired);
  }, [client]);

  const value = useMemo<AuthContextValue>(
    () => ({
      status,
      user,
      endedReason,
      login,
      logout,
      can: (permission, propertyId) => can(user, permission, propertyId),
      canAll: (permissions) => canAll(user, permissions),
      canAny: (permissions) => canAny(user, permissions),
      hasModule: (module) => hasModule(user, module),
      mfaChallengeToken,
      setupMfa,
      enrollMfa,
      verifyMfa,
      recoverMfa,
      cancelMfa,
      forgotPassword,
      verifyOtp,
      resetPassword,
    }),
    [
      status,
      user,
      endedReason,
      login,
      logout,
      mfaChallengeToken,
      setupMfa,
      enrollMfa,
      verifyMfa,
      recoverMfa,
      cancelMfa,
      forgotPassword,
      verifyOtp,
      resetPassword,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === null) {
    throw new Error('useAuth must be used inside an <AuthProvider>');
  }
  return context;
}
